import type maplibregl from "maplibre-gl";

import { PMTILES_SOURCE_LAYERS } from "../config/deforestation";
import type {
  LgaInfo,
  ParcelInfo,
  PlanningBundle,
  PropertyInfo,
} from "../types/deforestation";
import { getParcelStats } from "../utils/parcel-stats";

function parsePlanning(raw: unknown): PlanningBundle | null {
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== "object") return null;
    const p = parsed as Record<string, unknown>;
    return {
      zoneCode: typeof p.zoneCode === "string" ? p.zoneCode : undefined,
      primaryZoneDescription:
        typeof p.primaryZoneDescription === "string"
          ? p.primaryZoneDescription
          : undefined,
      psmaDescription:
        typeof p.psmaDescription === "string" ? p.psmaDescription : undefined,
    };
  } catch {
    return null;
  }
}

function lgaFromProps(props: Record<string, unknown>): LgaInfo | null {
  const name = typeof props.lgaName === "string" ? props.lgaName : null;
  const lgaPid = typeof props.lgaPid === "string" ? props.lgaPid : null;
  if (!name || !lgaPid) return null;
  return {
    name,
    lgaPid,
    abbreviatedName:
      typeof props.lgaAbbreviatedName === "string"
        ? props.lgaAbbreviatedName
        : null,
    state: typeof props.lgaState === "string" ? props.lgaState : "",
  };
}

function propertyFromProps(props: Record<string, unknown>): PropertyInfo | null {
  const propertyPid =
    typeof props.propertyPid === "string" ? props.propertyPid : null;
  if (!propertyPid) return null;
  const areaM2 =
    typeof props.propertyArea === "number"
      ? props.propertyArea
      : Number(props.propertyArea) || 0;
  const count =
    typeof props.propertyCadastrePidsCount === "number"
      ? props.propertyCadastrePidsCount
      : Number(props.propertyCadastrePidsCount) || 0;
  return {
    propertyPid,
    area_ha: areaM2 / 10000,
    cadastrePidsCount: count,
    state: typeof props.propertyState === "string" ? props.propertyState : "",
  };
}

export function buildParcelInfo(
  feature: maplibregl.GeoJSONFeature,
): ParcelInfo | null {
  const props = (feature.properties ?? {}) as Record<string, unknown>;
  const cadastrePid =
    typeof props.cadastrePid === "string" ? props.cadastrePid : null;
  if (!cadastrePid) return null;
  const parcelId =
    typeof props.parcelId === "string" && props.parcelId.length > 0
      ? props.parcelId
      : cadastrePid;
  const areaM2 =
    typeof props.area === "number" ? props.area : Number(props.area) || 0;

  return {
    parcel_id: parcelId,
    cadastrePid,
    cadastrePolygonPid:
      typeof props.cadastrePolygonPid === "string"
        ? props.cadastrePolygonPid
        : null,
    lot: typeof props.lot === "string" ? props.lot : null,
    plan: typeof props.plan === "string" ? props.plan : null,
    state: typeof props.state === "string" ? props.state : "",
    parcelType: typeof props.parcelType === "string" ? props.parcelType : "",
    titleStatus:
      typeof props.titleStatus === "string" ? props.titleStatus : "",
    area_ha: areaM2 / 10000,
    source: typeof props.source === "string" ? props.source : "",
    planning: parsePlanning(props.planningBundle),
    lga: lgaFromProps(props),
    property: propertyFromProps(props),
    stats: getParcelStats(cadastrePid),
  };
}

// Resolve a parcel by cadastrePid against the cadastre PMTiles. Used by the
// search dropdown, where we only have identity fields and need to enrich
// via the live map state. Returns null if the cadastre tile for that
// parcel isn't currently rendered.
export function resolveParcelByCadastrePid(
  m: maplibregl.Map,
  cadastrePid: string,
): ParcelInfo | null {
  if (!m.getSource("geoscape-cadastre")) return null;
  const features = m.querySourceFeatures("geoscape-cadastre", {
    sourceLayer: PMTILES_SOURCE_LAYERS.cadastre,
    filter: ["==", "cadastrePid", cadastrePid],
  });
  if (features.length === 0) return null;
  return buildParcelInfo(features[0]);
}
